import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  Alert,
  Snackbar,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PageHeaderNavigation from '../components/common/PageHeaderNavigation';
import ReportingStyleCard from '../components/rxf/ReportingStyleCard';
import { useAppSelector, useAppDispatch } from '../hooks/reduxHooks';
import {
  fetchUserPreferences,
  updateTogglePreference,
  fetchEffectivePrompt,
  updateCustomPrompt,
  selectUserPreferences,
  selectUseCustomTemplateByDefault,
  selectEffectivePredefinedPrompt,
  selectHasCustomPromptOverride,
  selectUserPreferencesLoading,
  selectUserPreferencesError,
  selectDarkMode,
  clearError,
  toggleDarkMode,
} from '../store/userPreferencesSlice';
import type { User } from '../store/authSlice';
import { updateUserProfile } from '../store/authSlice';
import { typography } from '~/theme';

const LOCALES = [
  { code: 'en-US', label: 'English (US)' },
  { code: 'en-GB', label: 'English (UK)' },
];

const UserPreferencesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state: { auth: { user: User | null } }) => state.auth.user);
  const preferences = useAppSelector(selectUserPreferences);
  const useCustomTemplateByDefault = useAppSelector(selectUseCustomTemplateByDefault);
  const effectivePrompt = useAppSelector(selectEffectivePredefinedPrompt);
  const hasCustomPromptOverride = useAppSelector(selectHasCustomPromptOverride);
  const darkMode = useAppSelector(selectDarkMode);
  const loading = useAppSelector(selectUserPreferencesLoading);
  const error = useAppSelector(selectUserPreferencesError);

  const isAdmin = user?.role === 'admin';

  // Local state for editing
  const [localToggle, setLocalToggle] = useState(false);
  const [localPrompt, setLocalPrompt] = useState('');
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Load preferences on mount
  useEffect(() => {
    dispatch(fetchUserPreferences());
    dispatch(fetchEffectivePrompt());
  }, [dispatch]);

  // Sync local state with Redux
  useEffect(() => {
    setLocalToggle(useCustomTemplateByDefault);
  }, [useCustomTemplateByDefault]);

  useEffect(() => {
    setLocalPrompt(effectivePrompt);
  }, [effectivePrompt]);

  const handleToggleChange = async (checked: boolean) => {
    setLocalToggle(checked); // optimistic update
    try {
      await dispatch(updateTogglePreference(checked)).unwrap();
      setShowSuccess(true);
    } catch {
      setLocalToggle(useCustomTemplateByDefault); // revert on failure
    }
  };

  const handleSavePrompt = async () => {
    if (!isAdmin) return;
    try {
      await dispatch(updateCustomPrompt(localPrompt || null)).unwrap();
      setIsEditingPrompt(false);
      setShowSuccess(true);
    } catch {
      // error is surfaced via Redux error state + Snackbar
    }
  };

  const handleResetPrompt = async () => {
    if (!isAdmin) return;
    try {
      await dispatch(updateCustomPrompt(null)).unwrap();
      setLocalPrompt(effectivePrompt); // sync with newly reset effective prompt
      setShowSuccess(true);
    } catch {
      // error is surfaced via Redux error state + Snackbar
    }
  };

  const handleCancelEdit = () => {
    setLocalPrompt(effectivePrompt);
    setIsEditingPrompt(false);
  };

  const handleLocaleChange = async (locale: string) => {
    if (!user) return;
    try {
      await dispatch(updateUserProfile({ userId: user.id, payload: { locale } })).unwrap();
      setShowSuccess(true);
    } catch {
      // error surfaced via auth error state
    }
  };

  if (loading && !preferences) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <PageHeaderNavigation
        title="User Preferences"
        subtitle="Manage your personal settings and template preferences"
      />

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          Preferences saved successfully
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => dispatch(clearError())}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => dispatch(clearError())}>
          {error}
        </Alert>
      </Snackbar>

      {/* Appearance Card */}
      <Card sx={{ mt: 3 }}>
        <CardHeader
          title="Appearance"
          subheader="Customize the visual theme"
        />
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={darkMode}
                onChange={() => dispatch(toggleDarkMode())}
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  Dark Mode
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Switch between light and dark themes
                </Typography>
              </Box>
            }
          />

          <Divider sx={{ my: 2 }} />

          <Box>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              Language & Region
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Select regional spelling and terminology preference
            </Typography>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="locale-select-label">Locale</InputLabel>
              <Select
                labelId="locale-select-label"
                value={user?.locale || 'en-US'}
                label="Locale"
                onChange={(e) => handleLocaleChange(e.target.value)}
              >
                {LOCALES.map((l) => (
                  <MenuItem key={l.code} value={l.code}>
                    {l.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Phase 1 RXF: Reporting Style */}
      <ReportingStyleCard />

      {/* Template Preferences Card */}
      <Card sx={{ mt: 3 }}>
        <CardHeader
          title="Template Preferences"
          subheader="Configure your default template selection mode"
        />
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={localToggle}
                onChange={(e) => handleToggleChange(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  Use Custom Template by Default
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  When enabled, the TranscriptionPage will default to custom template mode
                </Typography>
              </Box>
            }
          />
        </CardContent>
      </Card>

      {/* Custom Template Prompt Override Card (Admin Only) */}
      <Card sx={{ mt: 3 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>Predefined Prompt Override</span>
              {!isAdmin && (
                <Chip
                  size="small"
                  label="Admin Only"
                  color="default"
                  icon={<AdminPanelSettingsIcon fontSize="small" />}
                />
              )}
            </Box>
          }
          subheader="Customize the LLM guidance prompt used for custom templates"
        />
        <CardContent>
          {!isAdmin ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Only administrators can modify the predefined prompt. Contact an admin if you need changes.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Admin:</strong> Changes to this prompt affect how the LLM processes custom templates for your user account.
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2">Current Effective Prompt</Typography>
              {hasCustomPromptOverride ? (
                <Chip size="small" label="Custom Override" color="primary" />
              ) : (
                <Chip size="small" label="System Default" color="default" />
              )}
            </Box>
          </Box>

          {isEditingPrompt && isAdmin ? (
            <Box>
              <TextField
                fullWidth
                multiline
                rows={12}
                value={localPrompt}
                onChange={(e) => setLocalPrompt(e.target.value)}
                disabled={preferences.promptSaving}
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                    fontSize: typography.body2.fontSize,
                  },
                }}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  disabled={preferences.promptSaving}
                >
                  Cancel
                </Button>
                {hasCustomPromptOverride && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleResetPrompt}
                    disabled={preferences.promptSaving}
                    startIcon={<RefreshIcon />}
                  >
                    Reset to Default
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={handleSavePrompt}
                  disabled={preferences.promptSaving || !localPrompt.trim()}
                  startIcon={preferences.promptSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                >
                  Save Prompt
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  p: 2,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                  color: 'text.primary',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: typography.body2.fontSize,
                  whiteSpace: 'pre-wrap',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                {effectivePrompt || 'Loading...'}
              </Box>
              {isAdmin && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                  {hasCustomPromptOverride && (
                    <Tooltip title="Reset to system default prompt">
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleResetPrompt}
                        disabled={preferences.promptSaving}
                        startIcon={<RefreshIcon />}
                      >
                        Reset to Default
                      </Button>
                    </Tooltip>
                  )}
                  <Button
                    variant="contained"
                    onClick={() => setIsEditingPrompt(true)}
                    startIcon={<SaveIcon />}
                  >
                    Edit Prompt
                  </Button>
                </Box>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="caption" color="text.secondary">
            This predefined prompt guides the LLM when processing custom templates. It instructs the model
            on how to structure findings, handle subheadings, and format the impression section.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserPreferencesPage;
